/**
 * The planned-work rule for a date that names its weekday: "Tue 13/10/2026 · Wk A" in the cell, "Tuesday, 13 October
 * 2026 · Week A · ISO week 42" on hover. Visit and Task history cells both read it, so a day reads the same on every
 * planned-work board. Week A/B is the server's `weekCycle` (the engine's fortnight rule) and shows only on lines with
 * an A/B task (WBS-12805); an ISO odd/even line shows nothing extra in the cell, since the ISO week on hover says it.
 */
import { MetadataEntityFieldType, RegionSetting } from '@jms/model';
import type { ScheduleWeekCycle } from '@jms/model/scheduling';
import { DateTimeFormatPipe } from '@workbuddy/ui/fields/luxon.calendar';
import { timezoneForFieldType } from '@workbuddy/ui/v2/components/primitives/value/wall.clock.date.timezone';

/** The tenant's region settings a weekday date prints with. */
export interface WeekdayDateRegion {
  /** The clock a DateTime is shown on: the record's own zone when it has one, else the tenant's. Ignored for a Date. */
  timezone: string;
  dateFormat: string;
  timeFormat: string;
}

/** What a weekday date cell draws. */
export interface WeekdayDateLabel {
  /** Short weekday, then the value in the tenant's formats, then the fortnight week when known: `Tue 13/10/2026 09:00 AM · Wk A`. */
  text: string;
  /** The long form, one fact per line; a DateTime adds its time and zone: `Tuesday 13 Oct 2026\n09:00 AM · Sydney\nWeek A · ISO week 42`. */
  tooltip: string;
}

/**
 * The region a weekday date prints with: the record's own zone when it names one (a Visit is attended in its
 * site's zone), else the tenant's, with the same fallbacks wb-value uses.
 */
export function weekdayDateRegion(tenant: RegionSetting, recordTimezone?: unknown): WeekdayDateRegion {

  const own = typeof recordTimezone === 'string' && recordTimezone ? recordTimezone : null;
  return {
    timezone: own ?? tenant.timezone ?? 'UTC',
    dateFormat: tenant.dateFormat ?? 'dd/MM/yyyy',
    timeFormat: tenant.timeFormat ?? 'HH:mm',
  };
}

/** The city part of an IANA zone for the tooltip: `Australia/Sydney` → `Sydney`, `UTC` stays `UTC`. */
function zoneCity(zone: string): string {

  return zone.split('/').pop()!.replace(/_/g, ' ');
}

/** Tooltip lines render one per line: PrimeNG puts tooltip text in one element, so newlines need `pre-line`. */
export const WEEKDAY_DATE_TOOLTIP_CLASS = 'weekday-date-tooltip';

/** Global style for that class; the tooltip is appended to the body, outside any component's view. */
export const WEEKDAY_DATE_TOOLTIP_STYLE = `.${WEEKDAY_DATE_TOOLTIP_CLASS} .p-tooltip-text { white-space: pre-line; }`;

// The pipe owns how a stored value is read (bare local day vs instant); reusing it keeps that rule in one place.
const formatter = new DateTimeFormatPipe();

/**
 * The weekday label for a Date or DateTime value, or null when there is no valid value. A Date is a wall-clock day
 * and prints on the UTC day it is stored as (see `timezoneForFieldType`); a DateTime prints on `region.timezone`.
 * `weekCycle` is the row's week of its line's fortnight, for the value's own day; the ISO week is always on hover.
 */
export function weekdayDateLabel(value: unknown, type: MetadataEntityFieldType.Date | MetadataEntityFieldType.DateTime, region: WeekdayDateRegion, weekCycle?: ScheduleWeekCycle): WeekdayDateLabel | null {

  const zone = timezoneForFieldType(type, region.timezone);
  const day = formatter.transform(value, zone, `ccc ${region.dateFormat}`, '');
  if (!day) return null;
  // Luxon's `W` is the ISO week of the day printed, so W53 and W01 land on the right side of a year boundary.
  const weeks = [weekCycle ? `Week ${weekCycle}` : null, `ISO week ${formatter.transform(value, zone, 'W', '')}`].filter(Boolean).join(' · ');
  const cycle = weekCycle ? ` · Wk ${weekCycle}` : '';
  // Short lines, one fact each: a single sentence wrapped badly in the narrow tooltip.
  const longDay = formatter.transform(value, zone, 'cccc d MMM yyyy', '');
  if (type === MetadataEntityFieldType.Date) {
    return {
      text: `${day}${cycle}`,
      tooltip: [longDay, weeks].join('\n'),
    };
  }
  return {
    text: `${formatter.transform(value, zone, `ccc ${region.dateFormat} ${region.timeFormat}`, '')}${cycle}`,
    tooltip: [longDay, `${formatter.transform(value, zone, region.timeFormat, '')} · ${zoneCity(zone)}`, weeks].join('\n'),
  };
}
